import { localizedStrings } from '@core/strings'
import {
  ITemplateQuestion,
  TemplateQuestion,
} from '@core/entities/template/TemplateQuestion'
import Utils from '@core/utils/utils'
import moment from 'moment'
import {
  DOCUMENTS_ONLY,
  PHOTOS_ONLY,
} from '@core/entities/template/TemplateQuestion/types'
import { IPermitTemplateVersion } from '@core/entities/permit-template-editor'

// Parent permit date bounds (EAL Step 5 §3.2) — child dates must sit inside.
// Accepts a partial shape so callers can hand off a `PermitBase` directly.
type ParentDateRange = {
  startsOn?: string
  endsOn?: string
}

export const getTemplateStartDateRules = (parent?: ParentDateRange) => ({
  required: true,
  validate: (value: string) => {
    if (!parent?.startsOn || !value) return true
    if (moment(value).isBefore(parent.startsOn)) {
      return localizedStrings.childStartDateOutsideParent
    }
    return true
  },
})

export const getTemplateEndDateRules = (
  startsOn: string,
  template: IPermitTemplateVersion,
  parent?: ParentDateRange,
) => ({
  required: localizedStrings.pleaseSelectAnEndDate,
  validate: (value) => {
    if (parent?.endsOn && value && moment(value).isAfter(parent.endsOn)) {
      return localizedStrings.childEndDateOutsideParent
    }
    if (startsOn && template) {
      const startPlus30 = moment(startsOn).add(30, 'm')
      if (
        !template.calculateDurationBasedOnDays &&
        moment(value).diff(startPlus30) < 0
      ) {
        return localizedStrings.pleaseSelectDateAtLeast30MinutesAfterStart
      }

      if (
        !template.maxDurationSeconds &&
        !template.calculateDurationBasedOnDays
      ) {
        return true
      }

      if (template.calculateDurationBasedOnDays) {
        const dateDiff = moment(value).diff(moment(startsOn), 'days', true)

        return (
          dateDiff <= template.maxDurationDays ||
          localizedStrings.permitMaximumDurationDays(template.maxDurationDays)
        )
      } else {
        const maxDuration = Utils.secondsToDhm(template.maxDurationSeconds)
        const dateDiff = moment(value).diff(moment(startsOn), 'seconds')
        return (
          dateDiff <= template.maxDurationSeconds ||
          localizedStrings.permitMaximumDurationDhm(
            maxDuration.days,
            maxDuration.hours,
            maxDuration.minutes,
          )
        )
      }
    }
    return true
  },
})

export const AlphaNumericQuestionRules = {
  validate: ({ submissionText, required }: ITemplateQuestion) => {
    return (
      !required ||
      (required && !!submissionText) ||
      localizedStrings.thisFieldIsRequired
    )
  },
}

export const DateQuestionRules = {
  validate: ({ submissionText, required }: ITemplateQuestion) => {
    return (
      !required ||
      (required && !!submissionText) ||
      localizedStrings.thisFieldIsRequired
    )
  },
}

export const QuizQuestionRules = {
  validate: (question: ITemplateQuestion): string | true => {
    const selectedIds = new Set(
      (question.answers ?? []).filter((a) => a.selected).map((a) => a.id),
    )
    if (selectedIds.size === 0) {
      return localizedStrings.competencyInductionAnswerRequired
    }
    if (!TemplateQuestion.isCorrectQuizAnswer(question, selectedIds)) {
      return (
        question.invalidDescription ||
        localizedStrings.competencyInductionInvalidAnswerBody
      )
    }
    return true
  },
}

export const MultipleChoiceQuestionRules = {
  validate: ({ answers, minAnswers, maxAnswers }: ITemplateQuestion) => {
    const selectedAnswers = answers?.filter((v) => v.selected) ?? []
    const errors: string[] = []
    if (selectedAnswers.length > maxAnswers) {
      errors.push(localizedStrings.tooManySelected)
    }
    if (selectedAnswers.length < minAnswers) {
      errors.push(localizedStrings.tooFewSelected)
    }
    const hasMissingAdditionalInputs = answers?.some(
      ({ selected, requiresSubmissionText, submissionText }) =>
        selected && requiresSubmissionText && !submissionText,
    )
    if (hasMissingAdditionalInputs) {
      errors.push(localizedStrings.missingSomeAdditionalInputs)
    }
    if (errors.length === 0) return true
    return errors.join(', ')
  },
}

export const NumericQuestionRules = {
  validate: ({ submissionText, required }: ITemplateQuestion) => {
    return (
      !required ||
      (required && !!submissionText) ||
      localizedStrings.thisFieldIsRequired
    )
  },
}

export const DocumentQuestionRules = {
  validate: ({
    submissionDocuments,
    submissionProofs,
    required,
    allowedMediaTypes,
  }: ITemplateQuestion) => {
    if (!required) return true

    // Handle DOCUMENTS_ONLY case - only PDF attachments allowed
    if (allowedMediaTypes?.includes(DOCUMENTS_ONLY)) {
      return !!submissionDocuments?.length || localizedStrings.requiresProof
    }

    // Handle PHOTOS_ONLY case
    if (allowedMediaTypes?.includes(PHOTOS_ONLY)) {
      return !!submissionProofs?.length || localizedStrings.requiresProof
    }

    // Default case - both images and PDFs allowed
    return (
      !!submissionDocuments?.length ||
      !!submissionProofs?.length ||
      localizedStrings.requiresProof
    )
  },
}

export const PreconditionQuestionRules = {
  validate: ({
    submissionText,
    invalidAnswer,
    invalidDescription,
  }: ITemplateQuestion) => {
    if (submissionText === invalidAnswer) return invalidDescription
    return !!submissionText || localizedStrings.thisFieldIsRequired
  },
}

export const AcknowledgementQuestionRules = {
  validate: ({ acknowledged }: ITemplateQuestion) => {
    return acknowledged || localizedStrings.acknowledgeQuestionPrompt
  },
}

export const LinkPermitsQuestionRules = {
  validate: ({ linkedPermitsIds, required }: ITemplateQuestion) =>
    !required ||
    (required && !!linkedPermitsIds && linkedPermitsIds?.length > 0) ||
    localizedStrings.thisFieldIsRequired,
}

export const LinkRegisterItemsQuestionRules = {
  validate: ({ registerItemIds, required }: ITemplateQuestion) =>
    !required ||
    (required && !!registerItemIds && registerItemIds?.length > 0) ||
    localizedStrings.thisFieldIsRequired,
}

export const LinkChecklistsQuestionRules = {
  validate: ({ linkedChecklistsIds, required }: ITemplateQuestion) =>
    !required ||
    (required && !!linkedChecklistsIds && linkedChecklistsIds?.length > 0) ||
    localizedStrings.thisFieldIsRequired,
}

export const LinkInspectionsQuestionRules = {
  validate: ({ linkedInspectionsIds, required }: ITemplateQuestion) =>
    !required ||
    (required && !!linkedInspectionsIds && linkedInspectionsIds?.length > 0) ||
    localizedStrings.thisFieldIsRequired,
}

export const MAX_SUMMARY_CHARACTERS = 150
export const MAX_TASK_SUMMARY_CHARACTERS = 1000
export const TASK_SUMMARY_TRUNCATION_LENGTH = 100

export const CountdownQuestionRules = {
  validate: ({ countdownDuration, required }: ITemplateQuestion) => {
    return (
      !required ||
      (required && !!countdownDuration) ||
      localizedStrings.thisFieldIsRequired
    )
  },
}
